/**
 * The breadcrumb that tells the marketing page a visitor is already signed in.
 *
 * `duchef.app/` is sent to `/landing/` by a host-scoped rule in vercel.json, and
 * a redirect rule cannot know anything about the visitor: the Firebase session
 * lives in this origin's IndexedDB, which only the browser can read. So the app
 * leaves one synchronous key in localStorage, and the landing page reads it in
 * its <head> — before a single pixel of the story paints — and forwards a
 * signed-in visitor to `/?app=1`.
 *
 * localStorage is per-origin, which is exactly right here: the Firebase session
 * it stands for is per-origin too, so the flag only ever claims something about
 * the origin it was written on — never about a session the visitor has
 * somewhere else, which the app here could not use either.
 *
 * The key is duplicated in `public/landing/index.html` (plain static files, no
 * bundler between them and the browser, so it cannot be imported);
 * `scripts/__tests__/appEntryRedirect.test.ts` fails if the two ever drift.
 */

import { onAuthStateChanged } from 'firebase/auth';
import { auth, isFirebaseEnabled } from './firebaseConfig';

export const WEB_SESSION_FLAG_KEY = 'duchef.hasSession';

const storage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Safari in private mode, or storage blocked by policy.
    return null;
  }
};

/** Record — or retract — "this origin has a signed-in user". */
export const markWebSession = (hasSession: boolean): void => {
  const store = storage();
  if (!store) return;
  try {
    if (hasSession) store.setItem(WEB_SESSION_FLAG_KEY, '1');
    else store.removeItem(WEB_SESSION_FLAG_KEY);
  } catch {
    // A full or blocked quota costs the fast path, nothing else.
  }
};

/**
 * Keep the breadcrumb in step with Firebase for as long as the app is open.
 *
 * Deliberately its own subscription rather than a line inside the app's auth
 * effect: sign-out, token revocation and a session restored from cache all have
 * to move the flag, and this way none of them can be forgotten.
 */
export const startWebSessionFlagSync = (): (() => void) => {
  if (!storage() || !isFirebaseEnabled() || !auth) return () => {};
  return onAuthStateChanged(auth, (user) => markWebSession(!!user));
};
